import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    MessageSquare,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { buildPhoebeChatDebugSnapshot } from '@apps/web/components/phoebe_chat_debug_snapshot';
import {
    PhoebeAgentEventFeedChatSurface,
    usePhoebeAgentEventFeedChatViewState,
} from '@apps/web/components/phoebe_chat_view';
import {
    type OlderHistoryScroll,
    useOlderHistoryScroll,
} from '@apps/web/components/phoebe_chat_view/hooks/use_older_history_scroll';
import { IdentifierRedactionProvider } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { TabsContent } from '@apps/web/components/ui/tabs';
import {
    mergeAdminAgentRunEvents,
    useAdminAgentRunEventHistory,
} from '@apps/web/hooks/use_admin_agent_run_event_history';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { formatDelimitedLabel } from '@apps/web/lib/format_utils';
import { cn } from '@apps/web/lib/utils';
import {
    AgentTracePrimaryTabs,
    RUN_AGENT_TRACE_PRIMARY_TABS,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_primary_tabs';
import {
    RelatedRunLink,
    SubagentRunsPanel,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_related_runs';
import { AgentTraceTranscript } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_transcript_entry';
import { BraintrustExecutionLinks } from '@apps/web/routes/_app/admin/agent-traces/braintrust_execution_links';
import { CaregiverAgentJudgmentsPanel } from '@apps/web/routes/_app/admin/agent-traces/caregiver_agent_judgments_panel';
import { loadEphemeralRunNavigation } from '@apps/web/routes/_app/admin/agent-traces/ephemeral_run_navigation';
import { AgentTraceEpisodesPanel } from '@apps/web/routes/_app/admin/agent-traces/episode_linkage_panel';
import { SandboxFileExplorer } from '@apps/web/routes/_app/admin/agent-traces/sandbox_file_explorer';
import {
    useAdminAgentRun,
    useAdminAgentRunChildren,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentTraceChildRunRead,
    type AgentTraceRunCursorRead,
} from '@libraries/typescript/api_client';

const CHILDREN_PAGE_SIZE = 20;
const RUN_REFRESH_INTERVAL_MS = 3_000;
const DETAIL_PANEL_HEIGHT_CLASS = 'min-h-[36rem] lg:h-full lg:min-h-0';
const SCROLL_REGION_CLASS_NAME = 'min-h-0 overflow-y-auto';

export const Route = createFileRoute('/_app/admin/agent-traces/run/$runId')({
    component: AdminAgentRunTraceRoute,
    head: () => ({ meta: [{ title: 'Agent Run Trace' }] }),
});

function AdminAgentRunTraceRoute() {
    const { runId } = Route.useParams();
    return <AdminAgentRunTracePage runId={runId} />;
}

function mergeChildrenByRunId(
    firstPageChildren: Array<AgentTraceChildRunRead>,
    olderChildren: Array<AgentTraceChildRunRead>,
): Array<AgentTraceChildRunRead> {
    const firstPageRunIds = new Set(
        firstPageChildren.map((child) => child.run_id),
    );
    return [
        ...firstPageChildren,
        ...olderChildren.filter((child) => !firstPageRunIds.has(child.run_id)),
    ];
}

function useBusyRunRefresh({
    isBusy,
    refreshChildren,
    refreshRun,
    runId,
}: {
    readonly isBusy: boolean;
    readonly refreshChildren: () => Promise<unknown>;
    readonly refreshRun: () => Promise<unknown>;
    readonly runId: string;
}) {
    const previousRunStateRef = useRef({ isBusy: false, runId });

    useEffect(() => {
        const wasBusy =
            previousRunStateRef.current.runId === runId &&
            previousRunStateRef.current.isBusy;
        previousRunStateRef.current = { isBusy, runId };

        if (!isBusy) {
            if (wasBusy) {
                void Promise.all([refreshRun(), refreshChildren()]);
            }
            return;
        }

        const intervalId = window.setInterval(() => {
            void Promise.all([refreshRun(), refreshChildren()]);
        }, RUN_REFRESH_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [isBusy, refreshChildren, refreshRun, runId]);
}

function RunRenderedEventFeed({
    canOpenSubagentRun,
    fallbackRunStatus,
    onOpenSubagentRun,
    organizationId,
    events,
    pendingEvents,
    isLoadingHistory,
    isLoadingOlderHistory,
    hasOlderHistory,
    historyError,
    onLoadOlderHistory,
    onRetryHistory,
    runEventStream,
    runId,
}: {
    readonly canOpenSubagentRun: (childRunId: string) => boolean;
    readonly fallbackRunStatus: string;
    readonly onOpenSubagentRun: (childRunId: string) => void;
    readonly organizationId: string;
    readonly events: ReturnType<typeof usePhoebeAgentRunEventStream>['events'];
    readonly pendingEvents: ReturnType<
        typeof usePhoebeAgentRunEventStream
    >['pendingEvents'];
    readonly isLoadingHistory: boolean;
    readonly isLoadingOlderHistory: boolean;
    readonly hasOlderHistory: boolean;
    readonly historyError: Error | null;
    readonly onLoadOlderHistory: () => void;
    readonly onRetryHistory: () => void;
    readonly runEventStream: ReturnType<typeof usePhoebeAgentRunEventStream>;
    readonly runId: string;
}) {
    const chatState = usePhoebeAgentEventFeedChatViewState({
        events,
        pendingEvents,
        fallbackRunStatus: runEventStream.runState?.status ?? fallbackRunStatus,
        phoebeAgentRunId: runId,
        showGeneralOutreachProgressCards: true,
        showInlineDebugEvents: true,
        liveEventIds: runEventStream.liveEventIds,
    });
    const hasSnapshot =
        runEventStream.runState !== null ||
        events.length > 0 ||
        pendingEvents.length > 0;

    if ((runEventStream.isLoading || isLoadingHistory) && !hasSnapshot) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
                <Spinner size="sm" />
                Loading events…
            </div>
        );
    }

    if ((runEventStream.error || historyError) && !hasSnapshot) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                <span>Couldn’t load run events.</span>
                {historyError ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRetryHistory}
                    >
                        Try again
                    </Button>
                ) : null}
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            {runEventStream.connectionStatus === 'reconnecting' ||
            runEventStream.connectionStatus === 'error' ? (
                <div className="text-muted-foreground border-border flex h-9 shrink-0 items-center gap-2 border-b px-3 text-xs">
                    <Spinner size="xs" />
                    Reconnecting…
                </div>
            ) : null}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <IdentifierRedactionProvider redact={false}>
                    <PhoebeAgentEventFeedChatSurface
                        state={chatState}
                        canOpenSubagentRun={canOpenSubagentRun}
                        onSubmit={() => false}
                        readOnly={true}
                        organizationId={organizationId}
                        emptyState="No events yet."
                        olderHistoryScroll={{
                            identityKey: runId,
                            hasOlder: hasOlderHistory,
                            isLoading: isLoadingOlderHistory,
                            error: historyError,
                            loadOlder: onLoadOlderHistory,
                            retry: onRetryHistory,
                        }}
                        showGeneralOutreachProgressCards={true}
                        showRecommendationTraceProgress={true}
                        onOpenSubagentRun={onOpenSubagentRun}
                    />
                </IdentifierRedactionProvider>
            </div>
        </div>
    );
}

function RunRequestsPanel({
    historyScroll,
    isPartialHistory,
    organizationTimezone,
    trace,
}: {
    readonly historyScroll: OlderHistoryScroll;
    readonly isPartialHistory: boolean;
    readonly organizationTimezone: string;
    readonly trace: Parameters<typeof AgentTraceEpisodesPanel>[0]['trace'];
}) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const getScrollElement = useCallback(() => scrollRef.current, []);
    const getAnchorElements = useCallback(
        () =>
            Array.from(
                scrollRef.current?.querySelectorAll<HTMLElement>(
                    '[data-testid="agent-trace-episode"]',
                ) ?? [],
            ),
        [],
    );
    const { retry } = useOlderHistoryScroll({
        contentChangeKey: trace.events,
        contentRef: scrollRef,
        getAnchorElements,
        getScrollElement,
        history: historyScroll,
        initialScrollPosition: 'end',
    });
    const showHistoryStatus =
        historyScroll.hasOlder ||
        historyScroll.isLoading ||
        historyScroll.error !== null;

    return (
        <div
            className={cn(
                'bg-card flex overflow-hidden rounded-xl border',
                DETAIL_PANEL_HEIGHT_CLASS,
            )}
        >
            <div className="flex min-h-0 flex-1 flex-col">
                {showHistoryStatus ? (
                    <div className="text-muted-foreground flex min-h-9 shrink-0 items-center justify-center border-b px-3 text-xs">
                        {historyScroll.isLoading ? (
                            <span>Loading older events…</span>
                        ) : historyScroll.error ? (
                            <div className="flex items-center gap-2">
                                <span>Couldn’t load older events.</span>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={retry}
                                >
                                    Try again
                                </Button>
                            </div>
                        ) : (
                            <span>Scroll to the top for older events</span>
                        )}
                    </div>
                ) : null}
                <div
                    ref={scrollRef}
                    className={cn('space-y-3 pr-1', SCROLL_REGION_CLASS_NAME)}
                    role="region"
                    aria-label="Request history"
                    tabIndex={0}
                >
                    <AgentTraceEpisodesPanel
                        trace={trace}
                        organizationTimezone={organizationTimezone}
                        isPartialHistory={isPartialHistory}
                    />
                </div>
            </div>
        </div>
    );
}

export function AdminAgentRunTracePage({ runId }: { readonly runId: string }) {
    const listRunIds = useMemo(loadEphemeralRunNavigation, []);
    const navigate = useNavigate();
    const {
        data: run,
        error: runError,
        isLoading: isLoadingRun,
        mutate: refreshRun,
    } = useAdminAgentRun(runId);
    const {
        data: firstChildrenPage,
        error: childrenError,
        mutate: refreshFirstChildrenPage,
    } = useAdminAgentRunChildren(runId, {
        limit: CHILDREN_PAGE_SIZE,
    });
    const [loadMoreCursor, setLoadMoreCursor] =
        useState<AgentTraceRunCursorRead | null>(null);
    const [olderChildren, setOlderChildren] = useState<
        Array<AgentTraceChildRunRead>
    >([]);
    const [olderNextCursor, setOlderNextCursor] = useState<
        AgentTraceRunCursorRead | null | undefined
    >(undefined);
    const [loadMoreFailed, setLoadMoreFailed] = useState(false);
    const processedLoadMoreCursorRef = useRef<string | null>(null);
    const {
        data: loadMorePage,
        error: loadMoreError,
        isLoading: isLoadingMore,
        isValidating: isValidatingMore,
    } = useAdminAgentRunChildren(runId, {
        cursor: loadMoreCursor,
        enabled: loadMoreCursor !== null,
        limit: CHILDREN_PAGE_SIZE,
    });
    const history = useAdminAgentRunEventHistory({
        runId,
        enabled: run?.agent_conversation_id === null,
    });
    const runEventStream = usePhoebeAgentRunEventStream({
        organizationId: run?.organization_id ?? null,
        phoebeAgentRunId: runId,
        enabled: run?.agent_conversation_id === null && history.isReady,
        mode: run?.mode,
        variant: 'admin',
        pendingEventRetention: 'include-rejected',
        initialAfterOffset: history.forwardAfterOffset,
        initialSnapshot: false,
        excludeDrainedPendingEvents: true,
    });
    const events = useMemo(
        () =>
            mergeAdminAgentRunEvents(
                history.eventsNewestFirst,
                runEventStream.events,
            ),
        [history.eventsNewestFirst, runEventStream.events],
    );

    useEffect(() => {
        setLoadMoreCursor(null);
        setOlderChildren([]);
        setOlderNextCursor(undefined);
        setLoadMoreFailed(false);
        processedLoadMoreCursorRef.current = null;
    }, [runId]);

    useEffect(() => {
        if (!loadMoreCursor || !loadMorePage) {
            return;
        }
        const cursorKey = `${loadMoreCursor.created_at}:${loadMoreCursor.run_id}`;
        if (processedLoadMoreCursorRef.current === cursorKey) {
            return;
        }
        processedLoadMoreCursorRef.current = cursorKey;
        setOlderChildren((current) =>
            mergeChildrenByRunId(current, loadMorePage.children),
        );
        setOlderNextCursor(loadMorePage.next_cursor);
        setLoadMoreFailed(false);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMorePage]);

    useEffect(() => {
        if (!loadMoreCursor || !loadMoreError) {
            return;
        }
        setLoadMoreFailed(true);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMoreError]);

    useEffect(() => {
        if (!run?.agent_conversation_id) {
            return;
        }
        void navigate({
            to: '/admin/agent-traces/$conversationId',
            params: { conversationId: run.agent_conversation_id },
            replace: true,
        });
    }, [navigate, run?.agent_conversation_id]);

    useBusyRunRefresh({
        isBusy: runEventStream.isBusy,
        refreshChildren: refreshFirstChildrenPage,
        refreshRun,
        runId,
    });

    const children = useMemo(
        () =>
            mergeChildrenByRunId(
                firstChildrenPage?.children ?? [],
                olderChildren,
            ),
        [firstChildrenPage?.children, olderChildren],
    );
    const nextCursor =
        olderNextCursor === undefined
            ? (firstChildrenPage?.next_cursor ?? null)
            : olderNextCursor;
    const handleOpenSubagentRun = useCallback(
        (childRunId: string) => {
            const childRun = children.find(
                (candidate) => candidate.run_id === childRunId,
            );
            if (!childRun) {
                return;
            }
            if (childRun.conversation_id) {
                void navigate({
                    to: '/admin/agent-traces/$conversationId',
                    params: { conversationId: childRun.conversation_id },
                });
                return;
            }
            void navigate({
                to: '/admin/agent-traces/run/$runId',
                params: { runId: childRun.run_id },
            });
        },
        [children, navigate],
    );
    const canOpenSubagentRun = useCallback(
        (childRunId: string) =>
            children.some((child) => child.run_id === childRunId),
        [children],
    );

    if (isLoadingRun) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }

    if (runError || !run) {
        return (
            <div className="text-muted-foreground py-12 text-center text-sm">
                Run not found.
            </div>
        );
    }

    if (run.agent_conversation_id) {
        return null;
    }

    const displayStatus = runEventStream.runState?.status ?? run.status;
    const listIndex = listRunIds.indexOf(runId);
    const previousRunId = listIndex > 0 ? listRunIds[listIndex - 1] : undefined;
    const nextRun =
        listIndex >= 0 && listIndex < listRunIds.length - 1
            ? listRunIds[listIndex + 1]
            : undefined;
    const debugSnapshot = buildPhoebeChatDebugSnapshot({
        conversationId: null,
        conversationTitle:
            run.title ?? run.organization_name ?? `Run ${run.id.slice(0, 8)}`,
        phoebeRunId: run.id,
        agentGeneration: null,
        eventReadEnabled: true,
        connectionStatus: runEventStream.connectionStatus,
        runState: runEventStream.runState,
        events,
        pendingEvents: runEventStream.pendingEvents,
    });

    return (
        <div className="flex min-w-0 gap-4 p-6 lg:h-svh lg:max-h-svh lg:min-h-0 lg:overflow-hidden">
            <div className="flex min-w-0 flex-1 flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
                <div className="flex shrink-0 items-center gap-3">
                    <Button asChild={true} variant="ghost" size="sm">
                        <Link
                            to="/admin/agent-traces/ephemeral"
                            aria-label="Back to agent traces"
                        >
                            <ArrowLeft className="size-4" />
                        </Link>
                    </Button>
                    <div className="flex items-center gap-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label="Previous"
                            disabled={!previousRunId}
                            onClick={() => {
                                if (previousRunId) {
                                    void navigate({
                                        to: '/admin/agent-traces/run/$runId',
                                        params: { runId: previousRunId },
                                    });
                                }
                            }}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {listIndex >= 0 ? (
                            <span className="text-muted-foreground text-xs tabular-nums">
                                {listIndex + 1} of {listRunIds.length}
                            </span>
                        ) : null}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label="Next"
                            disabled={!nextRun}
                            onClick={() => {
                                if (nextRun) {
                                    void navigate({
                                        to: '/admin/agent-traces/run/$runId',
                                        params: { runId: nextRun },
                                    });
                                }
                            }}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="truncate text-lg font-semibold">
                                {run.title ?? `Run ${run.id.slice(0, 8)}`}
                            </h1>
                            <Badge variant="secondary">
                                {formatDelimitedLabel(run.run_type)}
                            </Badge>
                            <Badge variant="outline">
                                {displayStatus.replace('_', ' ')}
                            </Badge>
                        </div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                            <span>
                                {run.organization_name ?? run.organization_id}
                            </span>
                            <span>
                                Created{' '}
                                {format(
                                    new Date(run.created_at),
                                    'MMM d, h:mm a',
                                )}
                            </span>
                            {run.client_surface ? (
                                <span>{run.client_surface}</span>
                            ) : null}
                            {run.caregiver_conversation_id ? (
                                <Link
                                    to="/conversations"
                                    search={{
                                        conversationId:
                                            run.caregiver_conversation_id,
                                        organizationId: run.organization_id,
                                        mode: run.mode,
                                    }}
                                    className="hover:text-foreground inline-flex items-center gap-1 underline underline-offset-2"
                                >
                                    <MessageSquare className="size-3" />
                                    Caregiver conversation
                                </Link>
                            ) : null}
                            {run.parent_run ? (
                                <RelatedRunLink
                                    run={run.parent_run}
                                    label="Parent run"
                                    variant="inline"
                                />
                            ) : null}
                            {children.length > 0 ? (
                                <Badge variant="secondary">
                                    {children.length} subagent
                                    {children.length === 1 ? '' : 's'}
                                </Badge>
                            ) : null}
                        </div>
                    </div>
                    <BraintrustExecutionLinks
                        executions={run.executions}
                        runBraintrustUrl={run.braintrust_run_url}
                    />
                </div>
                <AgentTracePrimaryTabs tabs={RUN_AGENT_TRACE_PRIMARY_TABS}>
                    <TabsContent
                        value="rendered-chat"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-card overflow-hidden rounded-xl border',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <RunRenderedEventFeed
                                canOpenSubagentRun={canOpenSubagentRun}
                                fallbackRunStatus={run.status}
                                onOpenSubagentRun={handleOpenSubagentRun}
                                organizationId={run.organization_id}
                                events={events}
                                pendingEvents={runEventStream.pendingEvents}
                                isLoadingHistory={history.isLoadingInitial}
                                isLoadingOlderHistory={history.isLoadingOlder}
                                hasOlderHistory={history.hasOlder}
                                historyError={history.error}
                                onLoadOlderHistory={history.loadOlder}
                                onRetryHistory={history.retry}
                                runEventStream={runEventStream}
                                runId={run.id}
                            />
                        </div>
                    </TabsContent>
                    <TabsContent value="files" className="overflow-auto pt-2">
                        <SandboxFileExplorer key={run.id} runId={run.id} />
                    </TabsContent>
                    <TabsContent
                        value="events"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-admin-surface grid overflow-hidden rounded-xl border',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <div className="min-h-0 overflow-hidden">
                                <AgentTraceTranscript
                                    snapshot={debugSnapshot}
                                    initialScrollPosition="end"
                                    isLoadingHistory={history.isLoadingInitial}
                                    hasOlderHistory={history.hasOlder}
                                    isLoadingOlderHistory={
                                        history.isLoadingOlder
                                    }
                                    historyError={history.error}
                                    onReachOlderHistory={history.loadOlder}
                                    onRetryHistory={history.retry}
                                />
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="episodes"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <RunRequestsPanel
                            trace={{
                                executions: run.executions,
                                events,
                                pending_events: runEventStream.pendingEvents,
                            }}
                            organizationTimezone={run.organization_timezone}
                            isPartialHistory={
                                !history.isReady || history.hasOlder
                            }
                            historyScroll={{
                                identityKey: run.id,
                                hasOlder: history.hasOlder,
                                isLoading: history.isLoadingOlder,
                                error: history.error,
                                loadOlder: history.loadOlder,
                                retry: history.retry,
                            }}
                        />
                    </TabsContent>
                    <TabsContent
                        value="subagents"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'space-y-3 pr-1',
                                SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            {childrenError ? (
                                <div className="text-destructive bg-card rounded-xl border p-4 text-sm">
                                    Couldn’t load related runs.
                                </div>
                            ) : null}
                            {loadMoreFailed ? (
                                <div className="text-destructive text-center text-sm">
                                    Couldn’t load more related runs.
                                </div>
                            ) : null}
                            <SubagentRunsPanel
                                parentRun={run.parent_run}
                                childRuns={children}
                                runType={run.run_type}
                            />
                            {nextCursor ? (
                                <div className="flex justify-center">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={
                                            loadMoreCursor !== null ||
                                            isLoadingMore ||
                                            isValidatingMore
                                        }
                                        onClick={() => {
                                            setLoadMoreFailed(false);
                                            setLoadMoreCursor(nextCursor);
                                        }}
                                    >
                                        {loadMoreCursor !== null ||
                                        isLoadingMore ||
                                        isValidatingMore ? (
                                            <Spinner size="sm" />
                                        ) : null}
                                        {loadMoreFailed
                                            ? 'Try Again'
                                            : 'Load More'}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </TabsContent>
                </AgentTracePrimaryTabs>
            </div>
            {run.run_type === 'caregiver_sms' ? (
                <aside className="hidden w-[25rem] shrink-0 self-stretch lg:flex">
                    <div className="bg-card border-border flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border">
                        <div className="border-border flex h-11 shrink-0 items-center border-b px-4">
                            <h2 className="text-sm font-semibold">Judgment</h2>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                            <CaregiverAgentJudgmentsPanel
                                judgments={run.caregiver_agent_judgments ?? []}
                            />
                        </div>
                    </div>
                </aside>
            ) : null}
        </div>
    );
}
